import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import momentPostAuditQueueApi from '../../api/momentPostAuditQueue';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 分页查询动态审核队列
export const fetchMomentPostAuditQueueList = createAsyncThunk(
  'momentPostAuditQueue/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        content,
        location,
        auditStatus,
        auditType,
        startTime,
        endTime,
        ...otherParams
      } = params;

      const queryParams = {
        page,
        pageSize,
        content,
        location,
        auditStatus,
        auditType,
        startTime,
        endTime,
        ...otherParams
      };

      const response = await momentPostAuditQueueApi.pageAuditQueues(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          auditTime: item.auditTime ? formatTimeArray(item.auditTime) : null,
          
          // 审核状态映射
          auditStatusText: {
          'AUDITING': '审核中',
          'APPROVED': '审核通过',
          'REJECTED': '审核驳回',
          'RESUBMITTED': '重新提交'
          }[item.auditStatus] || '未知状态',

          // 审核类型映射
          auditTypeText: {
            1: '新增',
            2: '编辑'
          }[item.auditType] || '未知类型',

          // 图片处理
          imageList: item.images || [],

          // 价格范围处理
          priceRange: item.expectedMinPrice && item.expectedMaxPrice 
            ? `¥${item.expectedMinPrice} - ¥${item.expectedMaxPrice}` 
            : '未指定价格范围'
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取动态审核队列列表失败' });
    }
  }
);

// 获取动态审核队列详情
export const fetchMomentPostAuditQueueDetail = createAsyncThunk(
  'momentPostAuditQueue/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await momentPostAuditQueueApi.getAuditQueueDetail(id);
      
      // 处理详情数据
      const detail = response.data;
      
      return {
        ...detail,
        createTime: formatTimeArray(detail.createTime),
        updateTime: formatTimeArray(detail.updateTime),
        auditTime: detail.auditTime ? formatTimeArray(detail.auditTime) : null,
        
        // 审核状态映射
        auditStatusText: {
          '审核中': '审核中',
          '审核通过': '审核通过',
          '审核驳回': '审核驳回',
          '重新提交': '重新提交'
        }[detail.auditStatus] || detail.auditStatus,

        // 审核类型映射
        auditTypeText: {
          1: '新增',
          2: '编辑'
        }[detail.auditType] || '未知类型',

        // 图片处理
        imageList: detail.images || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取动态审核队列详情失败' });
    }
  }
);

// 审核动态
export const auditMomentPostQueue = createAsyncThunk(
  'momentPostAuditQueue/audit',
  async ({ id, status, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await momentPostAuditQueueApi.auditMomentPostQueue(id, { 
        status, 
        reason 
      });
      
      // 刷新列表
      dispatch(fetchMomentPostAuditQueueList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '审核动态失败' });
    }
  }
);

// 删除动态审核队列
export const deleteMomentPostAuditQueue = createAsyncThunk(
  'momentPostAuditQueue/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await momentPostAuditQueueApi.deleteAuditQueue(id);
      
      // 刷新列表
      dispatch(fetchMomentPostAuditQueueList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除动态审核队列失败' });
    }
  }
);

const momentPostAuditQueueSlice = createSlice({
  name: 'momentPostAuditQueue',
  initialState: {
    auditQueues: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentAuditQueue: null,
    auditQueueDetail: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    setCurrentAuditQueue: (state, action) => {
      state.currentAuditQueue = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取动态审核队列列表
    builder.addCase(fetchMomentPostAuditQueueList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMomentPostAuditQueueList.fulfilled, (state, action) => {
      state.loading = false;
      state.auditQueues = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchMomentPostAuditQueueList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取动态审核队列详情
    builder.addCase(fetchMomentPostAuditQueueDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchMomentPostAuditQueueDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.auditQueueDetail = action.payload;
    });
    builder.addCase(fetchMomentPostAuditQueueDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload;
    });
  }
});

export const { setCurrentAuditQueue } = momentPostAuditQueueSlice.actions;
export default momentPostAuditQueueSlice.reducer;
